import { Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Column } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ShippingMethodEntity } from './shipping-method.entity';

@Index(
	'shipping_method_restriction_unique',
	['fkShippingMethodId', 'restrictionType', 'criteriaValue'],
	{ unique: true },
)
@Entity('shipping_method_restriction')
export class ShippingMethodRestrictionEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_shipping_method__id',
		unsigned: true,
	})
	public fkShippingMethodId!: number;

	/**
	 * The type of restriction: 'item', 'state', or 'dayOfWeek'.
	 * - item: restricts based on item attributes (isHazmat, isRefrigerated, isHeavy)
	 * - state: restricts based on shipping address state code
	 * - dayOfWeek: restricts based on the day of the week (0=Sun..6=Sat)
	 *
	 * The presence of a row means the criteria value IS allowed.
	 * No rows for a given type means no restriction (all values allowed).
	 */
	@Column('varchar', { name: 'restrictionType', length: 50 })
	public restrictionType!: string;

	/**
	 * The specific criteria value that is allowed.
	 * - For item type: the item field name (e.g., 'isHazmat', 'isRefrigerated', 'isHeavy')
	 * - For state type: the two-letter state code (e.g., 'CA', 'NY')
	 * - For dayOfWeek type: the day number as a string ('0' = Sunday through '6' = Saturday)
	 */
	@Column('varchar', { name: 'criteriaValue', length: 255 })
	public criteriaValue!: string;

	/**
	 * For item restrictions only: when true, this shipping method is only
	 * available when the order actually contains items of this type.
	 * When false (default), the method is available regardless of whether
	 * the item type is present — the row just indicates it CAN handle it.
	 */
	@Column({
		type: 'boolean',
		name: 'onlyWhenPresent',
		width: 1,
		default: () => "'0'",
	})
	public onlyWhenPresent!: boolean;

	@ManyToOne(
		() => ShippingMethodEntity,
		(shippingMethod) => shippingMethod.restrictions,
		{
			onDelete: 'CASCADE',
			onUpdate: 'CASCADE',
		},
	)
	@JoinColumn([{ name: 'fk_shipping_method__id', referencedColumnName: 'id' }])
	public shippingMethod!: ShippingMethodEntity;
}
